import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Checkbox,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  useTheme,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon, FileDownload, Email as EmailIcon, Delete as DeleteIcon } from '@mui/icons-material';
import newsletterService from '../../services/newsletterService';
import adminService from '../../services/adminService';
import { ButtonPill, panelSx } from '../ui';

const NewsletterManagement = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, ids: [] });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const res = await newsletterService.getSubscribers({ page: page + 1, per_page: rowsPerPage });
      const list = res?.subscribers ?? [];
      setSubscribers(list);
      setTotalCount(res?.pagination?.total_count ?? res?.pagination?.total ?? list.length);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setSubscribers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
    setSelected([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const filteredSubscribers = subscribers.filter(sub =>
    (sub.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (sub.name && sub.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = (event) => {
    setSelected(event.target.checked ? filteredSubscribers.map(s => s.id) : []);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await adminService.exportCsv('subscribers');
      setSnackbar({ open: true, message: 'Export started', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Export failed', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const runDelete = async (ids) => {
    if (!ids || ids.length === 0) return;
    try {
      setBulkLoading(true);
      const res = await adminService.bulkAction('subscribers', { ids, action: 'delete' });
      setSnackbar({ open: true, message: `${res.affected ?? ids.length} subscriber(s) deleted`, severity: 'success' });
      setSelected([]);
      await fetchSubscribers();
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Delete failed', severity: 'error' });
    } finally {
      setBulkLoading(false);
      setConfirmDelete({ open: false, ids: [] });
    }
  };

  const allSelected = filteredSubscribers.length > 0 && selected.length === filteredSubscribers.length;
  const someSelected = selected.length > 0 && selected.length < filteredSubscribers.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Newsletter Subscribers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your newsletter subscriber list
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <ButtonPill
            size="small"
            variant="outlined"
            startIcon={<FileDownload />}
            disabled={exporting}
            onClick={handleExport}
          >
            Export CSV
          </ButtonPill>
          <Button variant="contained" startIcon={<EmailIcon />}>
            Send Newsletter
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search subscribers by email or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {selected.length > 0 && (
        <Paper sx={{ ...panelSx(theme), mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {selected.length} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <ButtonPill
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={bulkLoading}
            onClick={() => setConfirmDelete({ open: true, ids: selected })}
          >
            Delete
          </ButtonPill>
        </Paper>
      )}

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  inputProps={{ 'aria-label': 'select all subscribers' }}
                />
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Subscribed Date</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Verified</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubscribers.map((subscriber) => {
              const isSelected = selected.includes(subscriber.id);
              return (
              <TableRow key={subscriber.id} hover selected={isSelected}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleSelect(subscriber.id)}
                    inputProps={{ 'aria-label': `select subscriber ${subscriber.id}` }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{subscriber.email}</Typography>
                </TableCell>
                <TableCell>{subscriber.name || 'N/A'}</TableCell>
                <TableCell>
                  <Chip label={subscriber.source || 'website'} size="small" />
                </TableCell>
                <TableCell>
                  {(subscriber.subscribed_at || subscriber.subscribedAt || subscriber.created_at || subscriber.createdAt)
                    ? new Date(subscriber.subscribed_at || subscriber.subscribedAt || subscriber.created_at || subscriber.createdAt).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={subscriber.status || 'active'}
                    color={getStatusColor(subscriber.status || 'active')}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={(subscriber.is_verified ?? subscriber.isVerified) ? 'Yes' : 'No'}
                    color={(subscriber.is_verified ?? subscriber.isVerified) ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setConfirmDelete({ open: true, ids: [subscriber.id] })}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        />
      </TableContainer>

      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, ids: [] })}>
        <DialogTitle>Delete subscribers</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {confirmDelete.ids.length} subscriber(s)? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, ids: [] })} disabled={bulkLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => runDelete(confirmDelete.ids)} disabled={bulkLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewsletterManagement;
